import { AxiosError } from "axios";
import { Permission, UserRole } from "react-ai-platform-auth";

import {
  AnswerDistributionChoices,
  ApiClientId,
  InputConfig,
  InputTypes,
  ItemId,
  Organization,
  OrganizationCreator,
  OrganizationId,
  OutputConfig,
  OutputTypes,
  UnsavedOrganizationUser,
} from "../types";

type SuccessfulResponse<T = null> = Readonly<{
  data: T;
  error: null;
}>;

type ErrorResponse = Readonly<{
  data: null;
  error: { message: string; reasonCode?: number };
}>;

export type APIResult<T = null> = SuccessfulResponse<T> | ErrorResponse;

export type ProjectResponse = Readonly<{
  id: string;
  name: string;
  status: string;
  ctime: string;
  mtime: string;
  description: string;
  preferredWorkerIds: ReadonlyArray<string>;
  inputConfigTypes: ReadonlyArray<InputTypes>;
  outputConfigTypes: ReadonlyArray<OutputTypes>;
}>;

export type CreateProjectResponse = Readonly<{
  id: string;
}>;

export type ProjectContributorResponse = Readonly<{
  workerId: string;
  done: number;
  itemLimit: number | null;
}>;

export type ItemUploadedResponse = Readonly<{
  created: number;
  skipped: number;
  failed: number;
}>;

export type RevisionBatchSummaryResponse = Readonly<{
  id: string;
  projectId: string;
  name: string;
  creatorEmail?: string; // deprecated - fetch from authal using creatorId when needed
  creatorId: string;
  ctime: string;
  mtime: string;
  isAutomated: boolean;
}>;

export type RevisionBatchDetailsResponse = Readonly<{
  id: string;
  projectId: string;
  name: string;
  instructions: string;
  preferredWorkerIds: ReadonlyArray<string>;
  creatorEmail?: string; // deprecated - fetch from authal using creatorId when needed
  creatorId: string;
  ctime: string;
  mtime: string;
  automatedConfig: Readonly<{
    itemPercentage: number;
  }>;
}>;

export type AddBulkRevisionsResponse = Readonly<{
  creations: number;
  unexpectedErrors: number;
  skippedIncorrect: number;
  skippedDuplicates: number;
}>;

export type RevisionBatchListResponse = Readonly<{
  results: ReadonlyArray<RevisionBatchSummaryResponse>;
}>;

export type OrganizationResponse = Readonly<{
  id: OrganizationId;
  name: string;
  creator: OrganizationCreator;
  ctime: string;
  mtime: string;
  notValidAfter?: string;
}>;

export type OrganizationSummaryResponse = Readonly<{
  id: OrganizationId;
  name: string;
}>;

export type OrganizationListResponse = Readonly<{
  results: ReadonlyArray<OrganizationResponse>;
}>;

export type OrganizationResult = APIResult<Organization>;

export type CreateOrganizationPayload = Readonly<{
  name: string;
  organizationUser: UnsavedOrganizationUser;
}>;

export type UserMembershipResponse = Readonly<{
  id: string;
  role: string;
  type: string;
  organization?: OrganizationSummaryResponse;
}>;

export type AgreementResponse = Readonly<{
  type: string;
  version: string;
  acceptedTime?: string;
}>;

export type UserProfileResponse = Readonly<{
  agreements: ReadonlyArray<AgreementResponse>;
}>;

type IdentityResponse = Readonly<{
  userId: string;
  provider: string;
}>;

type _UserBaseResponse = Readonly<{
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  lastAuthTime?: string;
  ctime: string;
  mtime: string;
}>;

export type UserSummaryResponse = _UserBaseResponse &
  Readonly<{
    memberships: ReadonlyArray<UserMembershipResponse>;
  }>;

export type UserDetailsResponse = _UserBaseResponse &
  Readonly<{
    isEditable: boolean;
    permissions: ReadonlyArray<Permission>; // TODO: remove
    identities: ReadonlyArray<IdentityResponse>; // TODO: remove
    memberships?: ReadonlyArray<UserMembershipResponse>; // TODO: make required once /me endpoint is updated
    profile?: UserProfileResponse;
  }>;

export type UserListRequestFilter = Readonly<{
  scope?: string;
  roles?: ReadonlyArray<UserRole>;
  emails?: ReadonlyArray<string>;
  names?: ReadonlyArray<string>;
  excludeIds?: ReadonlyArray<string>;
  includeIds?: ReadonlyArray<string>;
}>;

export type PaginationRequest = Readonly<{
  pageNumber: number;
  pageSize: number;
}>;

export type ApiClientListRequestFilter = Readonly<{
  scope: string;
}>;

export enum UserSortKey {
  ID = "id",
  FIRST_NAME = "first_name",
  LAST_NAME = "last_name",
  EMAIL = "email",
  LAST_AUTH_TIME = "last_auth_time",
}

export enum ApiClientSortKey {
  // @ts-ignore 2300
  ID = "api_client_id",
  NAME = "name",
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

export type SortPredicate<SortKeyType> = Readonly<{
  sortKey: SortKeyType;
  sortOrder: SortOrder;
}>;

export type UserSortPredicate = SortPredicate<UserSortKey>;
export type ApiClientSortPredicate = SortPredicate<ApiClientSortKey>;

export type UserListResponse = Readonly<{
  results: ReadonlyArray<UserSummaryResponse>;
  metadata?: Readonly<{
    hasNextPage?: boolean;
  }>;
}>;

type ApiClientSummaryResponse = Readonly<{
  id: ApiClientId;
  name: string;
}>;

export type ApiClientListResponse = Readonly<{
  results: ReadonlyArray<ApiClientSummaryResponse>;
}>;

export type AuthalErrorsList = ReadonlyArray<{
  [loc: string]: { msg: string; type: string };
}>;

export type AuthalErrorResponse =
  | Readonly<{
      errors: string | AuthalErrorsList;
    }>
  | Readonly<{ detail: string }>
  | string;

export type ProjectErrorResponse = Readonly<{ project_type: ReadonlyArray<string> }> | string;

export type AuthalError = AxiosError<AuthalErrorResponse>;

export type ProjectError = AxiosError<ProjectErrorResponse>;

export type ContributorJobResponse = ProjectResponse | RevisionBatchSummaryResponse;

export type TimeSpentResponse = Readonly<{
  workerId: string;
  workerName: string;
  low: string;
  median: string;
  high: string;
  itemCount: number;
}>;

export type ProjectProgressResponse = Readonly<{
  projectId: string;
  available: number;
  complete: number;
  pending: number;
  revisionsAdded: number;
  revisionsReviewed: number;
  revisionsApproved: number;
  revisionsAmended: number;
  date: string;
}>;

export type AnswerDistributionWorker = Readonly<{
  totalItems: number;
  workerId: string;
  workerName: string;
}>;

export type AnswerDistributionChoiceMap = Readonly<{
  choiceId: string;
  choiceName: string;
  workers: ReadonlyArray<AnswerDistributionWorker>;
}>;

export type AnswerDistributionListResponse = Readonly<{
  choices: ReadonlyArray<AnswerDistributionChoices>;
  answers: ReadonlyArray<AnswerDistributionChoiceMap>;
}>;

export type AssessmentSummaryResponse = Readonly<{
  id: string;
  name: string;
  assessmentCategory: string;
  ctime: string;
}>;

export type GroupAssessmentResponse = Readonly<{
  id: string;
  name: string;
  description: string;
  creator_id: string;
  ctime: string;
  mtime: string;
  assessments: ReadonlyArray<AssessmentSummaryResponse>;
}>;

export type CreateAssessmentResponse = Readonly<{
  id: string;
}>;

export type CreateAssessmentResult = Readonly<{
  data: CreateAssessmentResponse | null;
  errors: { [fields: string]: Array<string> } | null;
}>;

export type CreateGroupAssessmentResponse = Readonly<{
  id: string;
}>;

export type CreateGroupAssessmentResult = Readonly<{
  data: CreateGroupAssessmentResponse | null;
  errors: { [fields: string]: Array<string> } | null;
}>;

export type AssessmentResponse = Readonly<{
  id: string;
  name: string;
  assessmentCategory: string;
  isAutomatedScoring: boolean;
  description: string;
  instructions: string;
  inputConfigs: ReadonlyArray<InputConfig>;
  outputConfigs: ReadonlyArray<OutputConfig>;
  timeLimit: number;
  estimatedTime: number;
  rewardPerUnit: string;
  preferredWorkerIds: ReadonlyArray<string>;
  scopes: ReadonlyArray<string>;
  mtime: string;
}>;

export type AssessmentTestQuestionResponse = {
  itemId: ItemId;
  key: string;
  attempts: number;
  medianTime: number | null;
  averageScore: string | null;
};

export type AssessmentTestQuestionListResponse = Readonly<{
  results: ReadonlyArray<AssessmentTestQuestionResponse>;
  metadata: Readonly<{
    pageNumber: number;
    pageSize: number;
    pageCount: number;
    totalItems: number;
  }>;
}>;

export type EligibilityUserResponse = Readonly<{
  authalId: string;
  firstName: string;
  lastName: string;
  accountType: string;
  ofacStatus: string;
  email?: string;
  primaryLanguage?: string;
  location?: string;
}>;

export type EligibilityUserListResponse = Readonly<{
  results: ReadonlyArray<EligibilityUserResponse>;
  metadata?: Readonly<{
    hasNextPage?: boolean;
  }>;
}>;

export enum AssessmentSortKey {
  ID = "id",
  NAME = "name",
  CTIME = "ctime",
}

export type AssessmentSortPredicate = SortPredicate<AssessmentSortKey>;

export enum GroupAssessmentSortKey {
  ID = "id",
  NAME = "name",
  CTIME = "ctime",
}

export type GroupAssessmentSortPredicate = SortPredicate<GroupAssessmentSortKey>;

export type ReportResponse = Readonly<{
  id: string;
  context_id: string;
  context_type: string;
  report_type: string;
  format: string;
  requester: Readonly<{
    user_id: string;
    org_id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  }>;
  status: string;
  download_url: string;
  ctime: string;
  expire_at: string | null;
}>;
